import { Button } from "@/components/ui/button";
import { Row } from "@/components/ui/flex";
import { PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useKitData } from "@/data/use-kits-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { cn } from "@/lib/utils";
import { Skill } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGrid2Plus } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Popover } from "@radix-ui/react-popover";
import { sampleSize } from "lodash";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useAssistContext } from "../AssistContext";
import { SkillsPickerPanel } from "./SkillsPickerPanel";
import {
  SkillPickerPools,
  skillRequiresProjectKitLink,
} from "./skills-picker-lib";

export const ExpertSkillsSection: React.FC<{
  pools: SkillPickerPools;
  showGroup?: boolean;
  onSelect: (input: { skillUuid: string }) => void;
}> = ({ pools, showGroup = false, onSelect }) => {
  const { messageStage } = useAssistContext();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { linkSkill } = useKitData(groupUuid, kitUuid);
  const [open, setOpen] = useState(false);

  const sampledSkills = useMemo(() => {
    const { expertSkills } = pools;
    return expertSkills.length < 3 ? expertSkills : sampleSize(expertSkills, 3);
  }, [pools]);

  const hasMore =
    pools.kitOnlySkills.length > 0 ||
    pools.groupSkills.length > 0 ||
    pools.expertSkills.length > 3;

  async function handleSelect(input: {
    skillUuid: string;
    requiresKitLink: boolean;
  }) {
    if (input.requiresKitLink) {
      try {
        await linkSkill({ skillUuid: input.skillUuid });
      } catch (error) {
        console.error("Failed to link skill to kit:", error);
        toast.error("Failed to add skill to workbook");
        return;
      }
    }

    onSelect({ skillUuid: input.skillUuid });
    setOpen(false);
  }

  function Tag({
    skill,
    className = "",
  }: {
    skill: Skill;
    className?: string;
  }) {
    return (
      <Button
        variant="outline"
        size="sm"
        title={skill.name}
        className={cn("block max-w-full truncate", className)}
        onClick={() =>
          handleSelect({
            skillUuid: skill.uuid,
            requiresKitLink: skillRequiresProjectKitLink("expert"),
          })
        }
        disabled={messageStage !== "idle"}
      >
        {skill.name}
      </Button>
    );
  }

  return (
    <Row className="flex-wrap items-start gap-1 py-2">
      {sampledSkills.map((skill) => (
        <Tag skill={skill} key={skill.uuid} />
      ))}
      {hasMore && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={messageStage !== "idle"}
            >
              <FontAwesomeIcon icon={faGrid2Plus} />
              More Skills
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" side="top" className="w-[400px] p-2">
            <SkillsPickerPanel
              pools={pools}
              showGroup={showGroup}
              onSelect={handleSelect}
            />
          </PopoverContent>
        </Popover>
      )}
    </Row>
  );
};
